/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { space, flexbox, border, color, layout, position } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import Text from 'theme/text';
import ButtonBorder from 'theme/buttons/btn-border';
import Alert from 'theme/icons/alert';
import Reload from 'theme/icons/reload';
import Notification from 'theme/icons/notification';
import translations from 'translations';
import { postWithdrawalRequest } from 'redux-api/action/billing';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 390px;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 16px 16px 16px 32px;

  ${space}
  ${border}
  ${color}
  ${layout}

  & > .body {
    width: 100%;
    padding-right: 16px;
  }
`;

const Flex = styled.div`
  display: flex;

  ${flexbox}
  ${space}
  ${position}
`;

const Footer = styled.div`
  width: 100%;
  text-align: left;

  ${space}

  & > .divider {
    width: 100%;
    height: 1px;
    background-color: ${themeGet('colors.primary_100')};
    margin-bottom: 10px;
  }
`;

function BillingWithdrawalCard({ languageCode, style, lastWithdrawal, isWithrawAvailable }) {
  const dispatch = useDispatch();
  const [clicked, setClicked] = useState();

  const { isWaiting } = useSelector((state) => state.billing);

  const handleRequest = (e) => {
    e.preventDefault();
    dispatch(postWithdrawalRequest());
    setClicked(true);
  };

  return (
    <Wrapper bg="white" {...style}>
      {isWithrawAvailable && (
        <>
          <Flex flexDirection="row" justifyContent="space-between" position="relative">
            <Flex alignContent="flex-start">
              <Text mr="8px" mt="4px">
                <Notification />
              </Text>
              <Text textAlign="left">{translations[languageCode].WithdrawalAvailableText}</Text>
            </Flex>
          </Flex>

          <div className="body">
            <Text
              textAlign="left"
              my="xs"
              fontSize="label"
              lineHeight="label"
              fontWeight="normal"
              color="grey_400"
            >
              {translations[languageCode].WithdrawalAvailableNotes}
            </Text>
          </div>

          <Footer>
            <div className="divider" />
            <div className="wrap-btn">
              <ButtonBorder
                style={{
                  p: 5,
                  mr: 'm',
                  color: 'primary_300',
                }}
                type="button"
                label={translations[languageCode].WithdrawalAvailableButton}
                onClick={handleRequest}
                disabled={isWaiting || clicked}
              />
            </div>
            {clicked && (
              <Text
                textAlign="left"
                my="xs"
                fontSize="label"
                lineHeight="label"
                fontWeight="normal"
                color="grey_400"
              >
                {translations[languageCode].WithdrawalAvailableThanks}
              </Text>
            )}
          </Footer>
        </>
      )}
      {!isWithrawAvailable && lastWithdrawal.id && (
        <Flex flexDirection="row" justifyContent="space-between" position="relative">
          <Flex alignContent="flex-start" textAlign="left">
            {lastWithdrawal.status === 'requested' && (
              <>
                <Text mr="8px" mt="4px">
                  <Reload />
                </Text>
                <Text>
                  {translations[languageCode].WithdrawalRequested1}{' '}
                  <strong>{lastWithdrawal.requestedAt}</strong>,{' '}
                  {translations[languageCode].WithdrawalRequested2}{' '}
                  <strong>{lastWithdrawal.nextAt}</strong>.{' '}
                  {translations[languageCode].WithdrawalRequested3}
                </Text>
              </>
            )}
            {lastWithdrawal.status === 'processed' && (
              <Text>
                {translations[languageCode].WithdrawalProcessed1}{' '}
                <strong>{lastWithdrawal.processedAt}</strong>,{' '}
                {translations[languageCode].WithdrawalProcessed2}
              </Text>
            )}
            {lastWithdrawal.status === 'failed' && (
              <>
                <Text mr="8px" mt="4px">
                  <Alert />
                </Text>
                <Text>
                  {translations[languageCode].WithdrawalFailed1}{' '}
                  <strong>{lastWithdrawal.processedAt}</strong>,{' '}
                  {translations[languageCode].WithdrawalFailed2}{' '}
                  <strong>{lastWithdrawal.failureDescription}</strong>.{' '}
                  {translations[languageCode].WithdrawalFailed3}
                </Text>
              </>
            )}
            {lastWithdrawal.status === 'blocked' && (
              <>
                <Text mr="8px" mt="4px">
                  <Alert />
                </Text>
                <Text>
                  {translations[languageCode].WithdrawalFailed1}{' '}
                  <strong>{lastWithdrawal.processedAt}</strong>,{' '}
                  {translations[languageCode].WithdrawalBloked}
                </Text>
              </>
            )}
          </Flex>
        </Flex>
      )}
    </Wrapper>
  );
}

export default BillingWithdrawalCard;
